import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Link } from "@tanstack/react-router";
import {
  ArrowRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DownloadIcon,
  SendIcon,
  SmartphoneIcon,
} from "lucide-react";
import { useState } from "react";

import { HeroAvatar, useHeroLookup } from "@/features/matches";
import { cn } from "@/shared/lib";
import { Button } from "@/shared/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/shared/ui/dialog";

import { byBestThenTime, isClipReady } from "../../model/clipState";
import { formatClock } from "../../model/formatClock";
import type { HighlightsMatch, HighlightView } from "../../model/Highlight";
import {
  HighlightSummary,
  MatchHeadline,
  useHighlightMeta,
  useHighlightTitle,
} from "../HighlightParts";
import { ClipStrip } from "./ClipStrip";
import { ShareLink } from "./ShareLink";
import { useShareCaption } from "./useShareCaption";

const SHARE = msg`Share`;
const VERTICAL = msg`Vertical 9:16`;
const VERTICAL_HINT = msg`For TikTok, Shorts and Reels: the moment's title on top, the clip in the middle`;
const DOWNLOAD = msg`Download`;
const DOWNLOAD_WIDE = msg`Download 16:9 · 1080p`;
const TELEGRAM = msg`Send to Telegram`;
const CAPTION = msg`Caption for a post`;
const IN_MATCH = msg`{time} in the match`;
const POSITION = msg`{index} of {count}`;
const PREVIOUS = msg`Previous clip`;
const NEXT = msg`Next clip`;
const ANALYSIS = msg`Match analysis`;
const ANALYZE = msg`Analyze the match`;

type ClipDialogProps = {
  // Открытый клип; `null` — диалог закрыт.
  highlight: HighlightView | null;
  // Матч клипа из раздела: шапка с исходом и переходом в разбор, соседние клипы. Матча нет
  // в ответе — плеер без них.
  match?: HighlightsMatch;
  onOpen: (highlight: HighlightView) => void;
  onClose: () => void;
};

// Плеер и «Поделиться» по макетам design/prototype/HighlightsShare.dc.html и
// HighlightsPlayer.dc.html (18е): сверху матч клипа со ссылкой на разбор, слева клип со
// стрелками к соседним клипам матча и их лентой, справа вертикальная версия, ссылка,
// скачать 16:9, Telegram и подпись к посту.
export const ClipDialog = ({ highlight, match, onOpen, onClose }: ClipDialogProps) => (
  <Dialog
    open={highlight !== null}
    onOpenChange={(open) => {
      if (!open) {
        onClose();
      }
    }}
  >
    {highlight !== null && (
      <DialogContent className="max-h-[calc(100dvh-2rem)] gap-0 overflow-y-auto p-0 sm:max-w-[1180px]">
        {/* Свой ключ у каждого клипа: при переходе к соседнему клипу ссылка, «Скопировано»
            и ошибки не переезжают с прошлого. */}
        <ClipDialogBody key={highlight.id} highlight={highlight} match={match} onOpen={onOpen} />
      </DialogContent>
    )}
  </Dialog>
);

type ClipDialogBodyProps = {
  highlight: HighlightView;
  match: HighlightsMatch | undefined;
  onOpen: (highlight: HighlightView) => void;
};

const ClipDialogBody = ({ highlight, match, onOpen }: ClipDialogBodyProps) => {
  const { i18n } = useLingui();
  const title = useHighlightTitle();
  const meta = useHighlightMeta();
  const hero = useHeroLookup()(highlight.hero_id);
  const caption = useShareCaption()(highlight);
  const { clip } = highlight;
  // Ссылки на видео подписаны и меняются при каждом перечитывании раздела (событие, возврат на
  // вкладку): держим первые, иначе клип начинался бы заново посреди просмотра.
  const [video] = useState({ src: clip.video_url, poster: clip.poster_url });
  // Готовые клипы матча в том же порядке, что плитки раздела: лучший, потом по времени.
  const siblings = (match?.items ?? []).filter(isClipReady).sort(byBestThenTime);
  const index = siblings.findIndex((item) => item.id === highlight.id);
  const previous = index > 0 ? siblings[index - 1] : undefined;
  const next = index >= 0 ? siblings[index + 1] : undefined;
  const telegram =
    clip.share_url === null
      ? null
      : `https://t.me/share/url?url=${encodeURIComponent(clip.share_url)}&text=${encodeURIComponent(caption)}`;
  const navButton = cn(
    "absolute top-1/2 flex size-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-background/70 text-foreground outline-none hover:bg-background/90 focus-visible:ring-2 focus-visible:ring-accent/60",
  );
  return (
    <div className="flex flex-col">
      {match !== undefined && (
        <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2 border-b border-sidebar-active bg-sidebar py-3 pr-14 pl-4">
          <span className="min-w-0 flex-[999_1_260px]">
            <MatchHeadline match={match} size="small" />
          </span>
          <Button asChild variant="outline" className="h-10">
            <Link to="/matches/$matchId" params={{ matchId: match.match_id }}>
              {i18n._(match.analysis_id === null ? ANALYZE : ANALYSIS)}
              <ArrowRightIcon aria-hidden="true" />
            </Link>
          </Button>
        </div>
      )}

      <div className={cn("flex flex-wrap gap-[18px] p-4", match === undefined && "pr-12 sm:pr-4")}>
        <div className="flex min-w-0 flex-[999_1_560px] flex-col gap-3">
          <div className="relative">
            {video.src !== null && (
              // Звук клипа — звук игры, субтитров у него нет.
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <video
                src={video.src}
                poster={video.poster ?? undefined}
                controls
                autoPlay
                playsInline
                preload="metadata"
                className="aspect-video w-full rounded-[10px] bg-black"
              />
            )}
            {previous !== undefined && (
              <button
                type="button"
                aria-label={i18n._(PREVIOUS)}
                onClick={() => {
                  onOpen(previous);
                }}
                className={cn(navButton, "left-2.5")}
              >
                <ChevronLeftIcon aria-hidden="true" className="size-5" />
              </button>
            )}
            {next !== undefined && (
              <button
                type="button"
                aria-label={i18n._(NEXT)}
                onClick={() => {
                  onOpen(next);
                }}
                className={cn(navButton, "right-2.5")}
              >
                <ChevronRightIcon aria-hidden="true" className="size-5" />
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-baseline justify-between gap-x-3.5 gap-y-1 px-0.5">
            <span className="flex min-w-0 items-center gap-3">
              {match === undefined && (
                <HeroAvatar name={hero.name} iconUrl={hero.iconUrl} size={40} />
              )}
              <span className="flex min-w-0 flex-col leading-snug">
                <DialogTitle className="text-xl font-semibold">{title(highlight)}</DialogTitle>
                <DialogDescription asChild>
                  <span className="flex flex-col gap-0.5 text-sm text-subtle">
                    <span>
                      {match === undefined
                        ? meta(highlight)
                        : i18n._({
                            ...IN_MATCH,
                            values: { time: formatClock(highlight.game_time_s) },
                          })}
                    </span>
                    <HighlightSummary highlight={highlight} />
                  </span>
                </DialogDescription>
              </span>
            </span>
            {siblings.length > 1 && index >= 0 && (
              <span className="font-mono text-[13px] text-subtle">
                {i18n._({ ...POSITION, values: { index: index + 1, count: siblings.length } })}
              </span>
            )}
          </div>
          {siblings.length > 1 && (
            <ClipStrip clips={siblings} currentId={highlight.id} onOpen={onOpen} />
          )}
        </div>

        <div className="flex min-w-0 flex-[1_1_320px] flex-col gap-3">
          <span className="font-display text-2xl font-bold">{i18n._(SHARE)}</span>

          {clip.vertical_url !== null && (
            <div className="flex gap-3 rounded-xl border border-border-strong bg-background p-3">
              <span
                aria-hidden="true"
                className="flex h-[120px] w-[68px] shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-subtle"
              >
                <SmartphoneIcon className="size-7" />
              </span>
              <span className="flex min-w-0 flex-col gap-1.5">
                <span className="font-semibold">{i18n._(VERTICAL)}</span>
                <span className="text-sm text-muted">{i18n._(VERTICAL_HINT)}</span>
                <Button asChild className="mt-auto self-start">
                  <a href={clip.vertical_url} download>
                    <DownloadIcon aria-hidden="true" />
                    {i18n._(DOWNLOAD)}
                  </a>
                </Button>
              </span>
            </div>
          )}

          <ShareLink highlight={highlight} />

          <div className="flex flex-wrap gap-2">
            {clip.download_url !== null && (
              <Button asChild variant="outline" className="h-11 flex-[1_1_140px]">
                <a href={clip.download_url} download>
                  <DownloadIcon aria-hidden="true" />
                  {i18n._(DOWNLOAD_WIDE)}
                </a>
              </Button>
            )}
            {telegram !== null && (
              <Button asChild variant="outline" className="h-11 flex-[1_1_140px]">
                <a href={telegram} target="_blank" rel="noopener noreferrer">
                  <SendIcon aria-hidden="true" />
                  {i18n._(TELEGRAM)}
                </a>
              </Button>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-sm text-subtle">{i18n._(CAPTION)}</span>
            <span className="rounded-lg border border-border bg-background px-3 py-2.5 text-sm select-all">
              {caption}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
